/**
 * MediaPipe Face Landmarker, loaded entirely from this app's own origin.
 *
 * The bundle, its WebAssembly and the model are vendored under `public/vision` and hash-checked by
 * `fetch.mjs`; nothing here reaches a CDN, so the capture page works under `default-src 'self'` and
 * the library's own metrics posting is refused by the browser rather than by an option it does not
 * offer. See `public/vision/MANIFEST.md`.
 */
import type { FaceLandmarks } from '@optiflow/pd-vision';

/** The slice of `@mediapipe/tasks-vision` this app calls, declared here so the 26 MB package need not be a dependency. */
interface VisionModule {
  FilesetResolver: {
    forVisionTasks(wasmPath: string): Promise<unknown>;
    isSimdSupported(): Promise<boolean>;
  };
  FaceLandmarker: {
    createFromOptions(fileset: unknown, options: LandmarkerOptions): Promise<FaceLandmarkerHandle>;
  };
}

interface LandmarkerOptions {
  baseOptions: { modelAssetPath: string; delegate: 'GPU' | 'CPU' };
  runningMode: 'VIDEO' | 'IMAGE';
  numFaces: number;
  outputFaceBlendshapes: boolean;
  outputFacialTransformationMatrixes: boolean;
}

export interface FaceLandmarkerHandle {
  detectForVideo(source: CanvasImageSource, timestampMs: number): FaceLandmarkerResult;
  /** Still images only: a landmarker created in IMAGE mode answers this and not `detectForVideo`. */
  detect(source: CanvasImageSource): FaceLandmarkerResult;
  close(): void;
}

export interface FaceLandmarkerResult {
  faceLandmarks: { x: number; y: number; z: number }[][];
  faceBlendshapes: { categories: { categoryName: string; score: number }[] }[];
  facialTransformationMatrixes: { data: number[] }[];
}

export interface Landmarker {
  landmarker: FaceLandmarkerHandle;
  delegate: 'GPU' | 'CPU';
  simd: boolean;
}

export const MEDIAPIPE_VERSION = '1.0.1';
export const MODEL = 'face_landmarker.task float16/1';

/** Where the vendored files are served from, relative to the app's own origin. */
export const VISION_ASSETS = '/vision';

/** A full URL, so the dev server serves MediaPipe's own files as they are rather than rewriting the import. */
const at = (path: string): string => new URL(path, window.location.href).href;

/**
 * The landmarker, for a camera (VIDEO) or for a single still (IMAGE).
 *
 * Both modes share every other option — the model, two faces, blendshapes and the head transform — so
 * a photo is read by exactly the detector a camera frame is. Two faces matters: with one, MediaPipe
 * would quietly return a single face from a group photo, and the one-face rule could not see the other.
 */
export async function loadFaceLandmarker(
  wanted: 'GPU' | 'CPU' = 'GPU',
  mode: 'VIDEO' | 'IMAGE' = 'VIDEO',
): Promise<Landmarker> {
  const lib = (await import(
    /* @vite-ignore */ at(`${VISION_ASSETS}/tasks-vision/vision_bundle.mjs`)
  )) as VisionModule;
  const fileset = await lib.FilesetResolver.forVisionTasks(
    at(`${VISION_ASSETS}/tasks-vision/wasm`),
  );
  const simd = await lib.FilesetResolver.isSimdSupported();
  const make = (delegate: 'GPU' | 'CPU') =>
    lib.FaceLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: at(`${VISION_ASSETS}/face_landmarker.task`), delegate },
      runningMode: mode,
      numFaces: 2,
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
    });
  let delegate = wanted;
  let landmarker: FaceLandmarkerHandle;
  try {
    landmarker = await make(wanted);
  } catch (error) {
    if (wanted !== 'GPU') throw error;
    delegate = 'CPU';
    landmarker = await make('CPU');
  }
  return { landmarker, delegate, simd };
}

/** MediaPipe's result as the measurement reads it: normalised points, the two blink scores, the head transform. */
export function toFaces(res: FaceLandmarkerResult): FaceLandmarks[] {
  const score = (i: number, name: string): number | undefined =>
    res.faceBlendshapes[i]?.categories.find((c) => c.categoryName === name)?.score;
  return res.faceLandmarks.map((points, i) => ({
    points,
    blinkLeft: score(i, 'eyeBlinkLeft'),
    blinkRight: score(i, 'eyeBlinkRight'),
    matrix: res.facialTransformationMatrixes[i]?.data,
  }));
}
